import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Avatar, 
  Typography, 
  Paper,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL ;

// Styled components
const ParticipantsContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  backgroundColor: '#ffffff',
  border: '1px solid #f0f0f0',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  width: '100%' // Ensure full width
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: '1.25rem',
  color: '#2c3e50',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1)
}));

const ParticipantCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    borderColor: '#cbd5e1'
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  fontSize: '1rem',
  fontWeight: 600,
  backgroundColor: '#3b82f6',
  color: '#ffffff',
  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  border: '2px solid #ffffff'
}));

const ParticipantName = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: '0.8rem',
  color: '#374151',
  textAlign: 'center',
  lineHeight: 1.2,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const SectionSubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  color: '#9ca3af'
}));

const EventParticipants = ({ eventId, language = 'EN', refreshTrigger }) => {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isThai = language === 'TH';

  // Text translations
  const texts = {
    title: isThai ? 'ผู้เข้าร่วม (ทุกภาษา)' : 'Participants (All Languages)',
    going: isThai ? 'ผู้เข้าร่วม' : 'Going',
    interested: isThai ? 'ผู้สนใจ' : 'Interested',
    noParticipants: isThai ? 'ยังไม่มีผู้เข้าร่วม' : 'No participants yet',
    noGoing: isThai ? 'ยังไม่มีผู้เข้าร่วม' : 'No one is going yet',
    noInterested: isThai ? 'ยังไม่มีผู้สนใจ' : 'No one is interested yet',
    loadingError: isThai ? 'ไม่สามารถโหลดข้อมูลผู้เข้าร่วมได้' : 'Failed to load participants',
    allLanguageNote: isThai ? 'แสดงผู้เข้าร่วมจากทุกภาษา' : 'Showing participants from all language versions',
    clickToViewProfile: isThai ? 'คลิกเพื่อดูโปรไฟล์' : 'Click to view profile'
  };

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle participant click (navigate to profile)
  const handleParticipantClick = (participant) => {
    // Navigate to the user's profile page
    navigate(`/profile/${participant.user_id}`);
  };

  // Fetch participants data
  const fetchParticipants = useCallback(async () => {
    if (!eventId) {
      setError('No event ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`🔍 [EventParticipants] Fetching participants for event ${eventId} (all language versions)`);

      const response = await axios.get(`${API_URL}/events/${eventId}/participants`);
      
      console.log(`✅ [EventParticipants] Loaded participants from all language versions:`, response.data);
      
      // Handle new response format with master_event_id
      const participantsData = response.data.participants || { going: [], interested: [] };
      const masterEventId = response.data.master_event_id;
      
      if (masterEventId) {
        console.log(`🌐 [EventParticipants] Participants from master event ${masterEventId}:`, {
          going: participantsData.going.length,
          interested: participantsData.interested.length,
          total: participantsData.total || 0
        });
      }
      
      setParticipants(participantsData);
    } catch (err) {
      console.error('❌ [EventParticipants] Error fetching participants:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch participants on mount and when refreshTrigger changes
  useEffect(() => {
    console.log(`🔄 [EventParticipants] Refresh triggered (trigger: ${refreshTrigger}) for event ${eventId}`);
    fetchParticipants();
  }, [eventId, fetchParticipants, refreshTrigger]);


  // Render participant card
  const renderParticipant = (participant) => (
    <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1} key={participant.user_id}>
      <Tooltip title={`${participant.name} - ${texts.clickToViewProfile}`} arrow>
        <ParticipantCard onClick={() => handleParticipantClick(participant)}>
          <StyledAvatar
            src={participant.avatar_url || undefined}
            alt={participant.name}
          >
            {!participant.avatar_url && getInitials(participant.name)}
          </StyledAvatar>
          
          <ParticipantName>
            {participant.name}
          </ParticipantName>
        </ParticipantCard>
      </Tooltip>
    </Grid>
  );

  // Render participants section
  const renderParticipantsSection = (statusType, participantsList, sectionTitle) => {
    if (!participantsList || participantsList.length === 0) {
      return (
        <Box mb={3}>
          <SectionSubTitle>
            <PersonIcon fontSize="small" />
            {sectionTitle} (0)
          </SectionSubTitle>
          <EmptyState>
            <Typography>
              {statusType === 'going' ? texts.noGoing : texts.noInterested}
            </Typography>
          </EmptyState>
        </Box>
      );
    }

    return (
      <Box mb={3}>
        <SectionSubTitle>
          <PersonIcon fontSize="small" />
          {sectionTitle} ({participantsList.length})
        </SectionSubTitle>
        <Grid container spacing={1.5}>
          {participantsList.map(renderParticipant)}
        </Grid>
      </Box>
    );
  };

  // Loading state
  if (loading) {
    return (
      <ParticipantsContainer>
        <SectionTitle>
          <PeopleIcon />
          {texts.title}
        </SectionTitle>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: '#6b7280', 
            fontStyle: 'italic',
            mb: 3,
            fontSize: '0.85rem'
          }}
        >
          {texts.allLanguageNote}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: '#6b7280', fontSize: '0.9rem' }}>
            Loading participants...
          </Typography>
        </Box>
      </ParticipantsContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <ParticipantsContainer>
        <SectionTitle>
          <PeopleIcon />
          {texts.title}
        </SectionTitle>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: '#6b7280', 
            fontStyle: 'italic',
            mb: 3,
            fontSize: '0.85rem'
          }}
        >
          {texts.allLanguageNote}
        </Typography>
        <Alert severity="error" sx={{ fontSize: '0.9rem' }}>
          {texts.loadingError}: {error}
        </Alert>
      </ParticipantsContainer>
    );
  }

  // No participants state
  if (!participants || (participants.going.length === 0 && participants.interested.length === 0)) {
    return (
      <ParticipantsContainer>
        <SectionTitle>
          <PeopleIcon />
          {texts.title}
        </SectionTitle>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: '#6b7280', 
            fontStyle: 'italic',
            mb: 3,
            fontSize: '0.85rem'
          }}
        >
          {texts.allLanguageNote}
        </Typography>
        <EmptyState>
          <PersonIcon sx={{ fontSize: 36, color: '#d1d5db', mb: 1 }} />
          <Typography variant="h6" color="textSecondary" sx={{ fontSize: '1rem' }}>
            {texts.noParticipants}
          </Typography>
        </EmptyState>
      </ParticipantsContainer>
    );
  }

  // Main render
  return (
    <ParticipantsContainer>
      <SectionTitle>
        <PeopleIcon />
        {texts.title}
      </SectionTitle>
      
      {/* Subtitle indicating all language versions */}
      <Typography 
        variant="body2" 
        sx={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontStyle: 'italic',
          mb: 3,
          fontSize: '0.85rem'
        }}
      >
        {texts.allLanguageNote}
      </Typography>

      {/* Going participants */}
      {renderParticipantsSection('going', participants.going, texts.going)}

      {/* Interested participants */}
      {renderParticipantsSection('interested', participants.interested, texts.interested)}
    </ParticipantsContainer>
  );
};

export default EventParticipants;